import { CheckIcon, MenuIcon } from "lucide-react";

import { styled } from "simplydo/react/styled";
import { Button } from "simplydo/react/ui/button";
import { Checkbox } from "simplydo/react/ui/checkbox";
import { H3 } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import {
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
} from "simplydo/react/ui/modal";
import {
  Table,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from "simplydo/react/ui/table";

import { DndContext } from "components/lib/dnd/DndContext";
import { Sortable } from "components/lib/dnd/Sortable";
import { moveWithId } from "components/lib/dnd/dndHelpers";

import type { Column } from "./types";

type TableSettingsProps<ItemType extends Record<string, any>> = {
  open: boolean;
  onClose: () => void;
  columns: Column<ItemType>[];
  updateColumns: (newColumns: Column<ItemType>[] | null) => void;
};

const TableSettings = <ItemType extends Record<string, any>>({
  open,
  onClose,
  columns,
  updateColumns,
}: TableSettingsProps<ItemType>) => {
  const updateColumn = (key: string, newColumn: Column<ItemType>) => {
    const index = columns.findIndex((col) => col.key === key);
    if (index === -1) return;

    const newColumns = [...columns];
    newColumns[index] = newColumn;
    updateColumns(newColumns);
  };

  return (
    <DndContext
      onDragEnd={(event) => {
        updateColumns(
          moveWithId("key", columns, event).map((col, i) => ({
            ...col,
            order: i,
          })),
        );
      }}
    >
      <Modal open={open} closeOnDimmerClick onClose={onClose}>
        <ModalHeader>Update table</ModalHeader>
        <ModalContent scrolling>
          <H3>Selected columns</H3>
          <p>
            Table columns can be modified here. Use the toggle to only display
            information that is relevant to you. Table columns can also be
            reordered via drag and drop.
          </p>
          <p>These settings will be synchronised between devices.</p>
          <div style={{ padding: 10 }}>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHeaderCell>Columns</TableHeaderCell>
                  <TableHeaderCell collapsing></TableHeaderCell>
                </TableRow>
              </TableHeader>
              {columns.map((column, index) => (
                <Sortable
                  key={column.key}
                  index={index}
                  id={column.key}
                  attachToChild
                >
                  <TableRow>
                    <TableCell>
                      <Checkbox
                        toggle
                        checked={column.enabled}
                        onChange={(_e, check) => {
                          if (
                            (check.checked && !column.enabled) ||
                            (!check.checked && column.enabled)
                          ) {
                            updateColumn(column.key as string, {
                              ...column,
                              enabled: !!check.checked,
                            });
                          }
                        }}
                      >
                        {column.settingName ?? column.name}
                      </Checkbox>
                    </TableCell>
                    <TableCell>
                      <Icon
                        icon={MenuIcon}
                        style={{ marginLeft: 10, marginRight: 10 }}
                      />
                    </TableCell>
                  </TableRow>
                </Sortable>
              ))}
            </Table>
          </div>
        </ModalContent>
        <ModalActions>
          <Button onClick={() => updateColumns(null)}>Reset to default</Button>
          <Button color="primary" onClick={onClose}>
            <Icon icon={CheckIcon} />
            Done
          </Button>
        </ModalActions>
      </Modal>
    </DndContext>
  );
};

export default TableSettings;
